import Reactor from 'data/reactor';
import RosterGetters from 'data/roster/getters';
import StudentGetters from 'data/student/getters';
import PartnerGetters from 'data/partner/getters';
import DataUtils from 'data/utils';
import { Immutable, toImmutable } from 'nuclear-js';
import CommonValues from 'resources/common-values';

export function getStudentViewOrder(studentBid) {
    if (!studentBid || studentBid == '') {
        return 'hide';
    }
    const currentStudentBid = Reactor.evaluate(RosterGetters.selectedStudentBid);
    if (studentBid == currentStudentBid) {
        return 'current';
    }
    const previousStudent = Reactor.evaluate(StudentGetters.previousStudent(currentStudentBid));
    if (studentBid == previousStudent.get('studentBid', '')) {
        return 'previous';
    }
    const nextStudent = Reactor.evaluate(StudentGetters.nextStudent(currentStudentBid));
    if (studentBid == nextStudent.get('studentBid', '')) {
        return 'next';
    } else {
        return 'hide';
    }
}

export function getStudentInfoForTerm(studentBid, termNumber) {
    if (!studentBid || !termNumber) {
        return null;
    }
    const history = Reactor.evaluate(StudentGetters.termDataForStudentBid(studentBid));
    const infoForTerm = history ? history.find((si) => si.get('termNumber', '') == termNumber, null, null) : null;
    return infoForTerm;
}

export function getTypicalScoreForStudent(studentBid, subjectBid, ritQuintiles) {
    if (!studentBid || !subjectBid || !ritQuintiles) {
        return null;
    }

    //Find partner's current term, if it exists
    const currentTerm = Reactor.evaluate(PartnerGetters.currentTerm);

    //Find Student Info for current term, if it exists
    const studentInfoForTerm = currentTerm ? getStudentInfoForTerm(studentBid, currentTerm.get('termNumber')) : null;

    //Build a key to find the current term/subject/grade typical score
    const ritQuintileKey = studentInfoForTerm
        ? toImmutable({
              termNumber: currentTerm.get('termNumber'),
              subject: subjectBid,
              grade: studentInfoForTerm.get('grade'),
              weeks: studentInfoForTerm.get('instructionalWeek'),
          })
        : null;

    const ritQuintile = ritQuintileKey ? ritQuintiles.get(ritQuintileKey, null) : null;

    return ritQuintile ? ritQuintile.get(50) : null;
}

export function getSubjectToSelect(preferredSubject, selectedTermNumber, studentTestEvents, currentTerm) {
    const termIsMostRecent = selectedTermNumber == CommonValues.mostRecentTermNumber;
    const termIsCurrent = currentTerm && selectedTermNumber == currentTerm.get('termNumber');
    const preferredSubjectBid = preferredSubject.get('bid');
    const subjectIsOptional = CommonValues.OPTIONAL_SUBJECT_BIDS.includes(preferredSubjectBid);
    if ((termIsMostRecent || termIsCurrent) && !subjectIsOptional) {
        // Most Recent is selected - all subjects are valid or If it is a current term
        return preferredSubject;
    } else if ((studentTestEvents && !studentTestEvents.isEmpty() && termIsMostRecent) || termIsCurrent) {
        // Subject is optional, so verify that test events exist for it.
        const mostRecentTestEventsBySubject = DataUtils.findMostRecentTestEventsBySubject(studentTestEvents);

        const preferredSubjectTestEvent = mostRecentTestEventsBySubject.find(
            (testEvent) => testEvent.get('subject') == preferredSubjectBid
        );
        if (preferredSubjectTestEvent) {
            return preferredSubject;
        } else {
            const subjectBidToSelect = mostRecentTestEventsBySubject.first().get('subject', 0);
            return CommonValues.subjects.find((subject) => subject.get('bid') == subjectBidToSelect);
        }
    }

    if (DataUtils.findTestEventForTermAndSubject(studentTestEvents, selectedTermNumber, preferredSubject.get('bid'))) {
        // Selected subject has a test event for the selected term.
        return preferredSubject;
    }

    let subjectToSelect = Immutable.Map(); // Null won't work for a return value. We need to be able to do a 'get' on what is returned.
    const subjects = CommonValues.subjects;
    subjects.forEach((subject) => {
        if (DataUtils.findTestEventForTermAndSubject(studentTestEvents, selectedTermNumber, subject.get('bid', 0))) {
            // Iterated subject has a test event for the selected term.
            subjectToSelect = subject;
            return false;
        }
    });

    // If we've gotten this far with no selected subject, make it math
    if (subjectToSelect.isEmpty()) {
        subjectToSelect = subjects.find((subject) => subject.get('bid') === CommonValues.SUBJECT_BID_MATHEMATICS);
    }

    return subjectToSelect;
}

function getStudentInfoField(studentBid, fieldName) {
    const studentInfo = Reactor.evaluate(StudentGetters.studentInfo(studentBid));
    if (studentInfo && !studentInfo.isEmpty()) {
        return studentInfo.maxBy((si) => si.get('termNumber')).get(fieldName);
    }
    return null;
}

export function getStudentFirstName(studentBid) {
    return getStudentInfoField(studentBid, 'firstName');
}

export function getStudentGender(studentBid) {
    return getStudentInfoField(studentBid, 'gender');
}

export function populateHighlightsWithStudentInfo(studentBid, highlights) {
    if (highlights) {
        Object.keys(highlights).forEach((course) => {
            highlights[course].forEach((courseHighlights) => {
                courseHighlights.message = transformHighlights(studentBid, courseHighlights.message);
            });
        });
    }
    return highlights;
}

export function pruneOptionalSubjectsWithNoTestEvents(studentTestEvents) {
    const mostRecentTestEventsBySubject = DataUtils.findMostRecentTestEventsBySubject(studentTestEvents);
    const prunedSubjectList = CommonValues.subjects.filter((subject) => {
        const subjectBid = subject.get('bid', 0);
        if (!CommonValues.OPTIONAL_SUBJECT_BIDS.includes(subjectBid)) {
            return true;
        } else {
            const testEventForSubject = mostRecentTestEventsBySubject
                ? mostRecentTestEventsBySubject.find((testEvent) => testEvent.get('subject') == subjectBid)
                : false;
            return !!testEventForSubject;
        }
    });
    return prunedSubjectList;
}

const highlightsPronouns = {
    male: {
        Possessive: "'s",
        PronounPossessive: 'his',
        Pronoun: 'he',
        PronounObject: 'him',
        LinkingVerb: 'is',
    },
    female: {
        Possessive: "'s",
        PronounPossessive: 'her',
        Pronoun: 'she',
        PronounObject: 'her',
        LinkingVerb: 'is',
    },
    x: {
        Possessive: "'s",
        PronounPossessive: 'their',
        Pronoun: 'they',
        PronounObject: 'them',
        LinkingVerb: 'are',
    },
};

export function transformHighlights(studentBid, highlightsTemplate) {
    let populatedHighlights = highlightsTemplate.replace('{{FirstName}}', getStudentFirstName(studentBid));
    let pronoun = 'x'; // If gender is null or undefined, default is 'x'.
    switch (getStudentGender(studentBid)) {
        case 'FEMALE':
            pronoun = 'female';
            break;
        case 'MALE':
            pronoun = 'male';
            break;
    }
    Object.keys(highlightsPronouns[pronoun]).forEach((key) => {
        const expressionToMatch = `{+${key}}+`; // Handles double and triple handlebars ( {{ key }} and {{{ key }}} )
        const value = highlightsPronouns[pronoun][key];
        populatedHighlights = populatedHighlights.replace(new RegExp(expressionToMatch, 'g'), value);
    });
    return populatedHighlights;
}

export default {
    getStudentViewOrder,
    getStudentInfoForTerm,
    getTypicalScoreForStudent,
    getSubjectToSelect,
    getStudentFirstName,
    getStudentGender,
    populateHighlightsWithStudentInfo,
    pruneOptionalSubjectsWithNoTestEvents,
    transformHighlights,
};
